import React, { useState } from 'react';
import { UserPlus, Shield, ArrowLeft, Sparkles, Mail, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { validateRegisterForm, getPasswordStrength } from '../utils/validation';
import { sendVerificationCode, verifyCode } from '../utils/emailVerification';
interface PatientSignupPageProps {
  onBackToLogin: () => void;
}
export function PatientSignupPage({
  onBackToLogin
}: PatientSignupPageProps) {
  const {
    register
  } = useAuth();
  const [step, setStep] = useState<'form' | 'verify'>('form');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const passwordStrength = password ? getPasswordStrength(password) : null;
  const strengthColors = {
    weak: 'bg-red-400',
    medium: 'bg-amber-400',
    strong: 'bg-emerald-400',
    very_strong: 'bg-emerald-500'
  };
  const handleSendCode = async () => {
    setRegisterError('');
    setVerificationError('');
    const validation = validateRegisterForm(name, email, password, confirmPassword);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }
    setErrors({});
    setIsSendingCode(true);
    const result = await sendVerificationCode(email);
    if (result.success) {
      setStep('verify');
    } else {
      setRegisterError(result.error || 'Error al enviar código de verificación');
    }
    setIsSendingCode(false);
  };
  const handleVerifyCode = async () => {
    setVerificationError('');
    if (!verificationCode || verificationCode.length !== 6) {
      setVerificationError('Por favor ingresa el código de 6 dígitos');
      return;
    }
    setIsVerifying(true);
    const result = await verifyCode(email, verificationCode);
    if (result.success) {
      await handleRegister();
    } else {
      setVerificationError(result.error || 'Código incorrecto');
    }
    setIsVerifying(false);
  };
  const handleRegister = async () => {
    setIsLoading(true);
    const result = await register(name, email, password, 'paciente');
    if (!result.success) {
      setRegisterError(result.error || 'Error al crear la cuenta');
      setStep('form');
    }
    setIsLoading(false);
  };
  const handleResendCode = async () => {
    setVerificationError('');
    setIsSendingCode(true);
    const result = await sendVerificationCode(email);
    if (result.success) {
      setVerificationError('');
      setVerificationCode('');
    } else {
      setVerificationError(result.error || 'Error al reenviar código');
    }
    setIsSendingCode(false);
  };
  // Verification Step
  if (step === 'verify') {
    return <div className="min-h-screen bg-gradient-to-br from-cyan-50/50 via-white to-teal-50/30 flex items-center justify-center p-4" data-id="element-4831">
        <div className="w-full max-w-md" data-id="element-4832">
          <button onClick={() => setStep('form')} className="mb-8 flex items-center gap-2 text-gray-500 hover:text-teal-600 transition-colors group" data-id="element-4833">
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" data-id="element-4834" />
            <span className="text-sm font-medium" data-id="element-4835">Volver</span>
          </button>

          <div className="bg-white rounded-3xl shadow-lg shadow-teal-100/50 p-8 sm:p-10 border border-gray-100" data-id="element-4836">
            <div className="text-center mb-8" data-id="element-4837">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl shadow-lg shadow-teal-200/50 mb-6" data-id="element-4838">
                <Mail className="w-8 h-8 text-white" strokeWidth={2.5} data-id="element-4839" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4840">
                Verifica tu correo
              </h2>
              <p className="text-sm text-gray-600 mb-1" data-id="element-4841">Código enviado a</p>
              <p className="text-sm font-semibold text-teal-600" data-id="element-4842">{email}</p>
            </div>

            {verificationError && <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-2xl" data-id="element-4843">
                <p className="text-sm text-red-700 font-medium" data-id="element-4844">
                  {verificationError}
                </p>
              </div>}

            <div className="space-y-6" data-id="element-4845">
              <div data-id="element-4846">
                <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-4847">
                  Código de Verificación
                </label>
                <input type="text" maxLength={6} value={verificationCode} onChange={e => setVerificationCode(e.target.value.replace(/\D/g, ''))} className="w-full px-4 py-4 text-center text-2xl font-bold tracking-[0.5em] border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-teal-100 focus:border-teal-500 transition-all outline-none" placeholder="000000" autoFocus data-id="element-4848" />
                <p className="text-xs text-gray-500 mt-2 text-center" data-id="element-4849">
                  El código expira en 10 minutos
                </p>
              </div>

              <button onClick={handleVerifyCode} disabled={isVerifying || verificationCode.length !== 6} className="w-full h-12 text-base font-semibold bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white shadow-lg shadow-teal-200/50 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4850">
                {isLoading ? 'Creando cuenta...' : isVerifying ? 'Verificando...' : 'Verificar y Crear Cuenta'}
              </button>

              <div className="text-center pt-2" data-id="element-4851">
                <p className="text-sm text-gray-600 mb-2" data-id="element-4852">
                  ¿No recibiste el código?
                </p>
                <button onClick={handleResendCode} disabled={isSendingCode} className="text-sm text-teal-600 font-semibold hover:text-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-4853">
                  {isSendingCode ? 'Reenviando...' : 'Reenviar código'}
                </button>
              </div>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-center gap-2 text-xs text-gray-500" data-id="element-4854">
            <Shield className="w-4 h-4 text-teal-600" data-id="element-4855" />
            <span data-id="element-4856">Verificación segura de correo electrónico</span>
          </div>
        </div>
      </div>;
  }
  // Registration Form Step
  return <div className="min-h-screen bg-gradient-to-br from-cyan-50/50 via-white to-teal-50/30 flex items-center justify-center p-4" data-id="element-4857">
      <div className="w-full max-w-md" data-id="element-4858">
        <button onClick={onBackToLogin} className="mb-8 flex items-center gap-2 text-gray-500 hover:text-teal-600 transition-colors group" data-id="element-4859">
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" data-id="element-4860" />
          <span className="text-sm font-medium" data-id="element-4861">
            Volver al inicio de sesión
          </span>
        </button>

        <div className="text-center mb-8" data-id="element-4862">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl shadow-lg shadow-teal-200/50 mb-6" data-id="element-4863">
            <UserPlus className="w-8 h-8 text-white" strokeWidth={2.5} data-id="element-4864" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-4865">
            Crear Cuenta de Paciente
          </h1>
          <div className="flex items-center justify-center gap-2 text-teal-600" data-id="element-4866">
            
            <p className="text-sm font-medium" data-id="element-4867">Sistema Savidna</p>
            
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-lg shadow-teal-100/50 p-8 sm:p-10 border border-gray-100" data-id="element-4868">
          <div className="mb-8" data-id="element-4869">
            <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-4870">Bienvenido</h2>
            <p className="text-sm text-gray-600" data-id="element-4871">
              Crea tu cuenta para registrar tu información médica de forma
              segura
            </p>
          </div>

          {registerError && <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-2xl" data-id="element-4872">
              <p className="text-sm text-red-700 font-medium" data-id="element-4873">
                {registerError}
              </p>
            </div>}

          <form onSubmit={e => {
          e.preventDefault();
          handleSendCode();
        }} className="space-y-5" data-id="element-4874">
            <div data-id="element-4875">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4876">
                Nombre Completo
              </label>
              <input type="text" placeholder="Juan Pérez" value={name} onChange={e => setName(e.target.value)} className={`w-full px-4 py-3 border-2 rounded-xl transition-all outline-none focus:ring-4 focus:ring-teal-100 focus:border-teal-500 ${errors.name ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-4877" />
              {errors.name && <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1" data-id="element-4878">
                  {errors.name}
                </p>}
            </div>

            <div data-id="element-4879">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4880">
                Correo Electrónico
              </label>
              <input type="email" placeholder="correo@ejemplo.com" value={email} onChange={e => setEmail(e.target.value)} className={`w-full px-4 py-3 border-2 rounded-xl transition-all outline-none focus:ring-4 focus:ring-teal-100 focus:border-teal-500 ${errors.email ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-4881" />
              {errors.email && <p className="text-xs text-red-600 mt-1.5" data-id="element-4882">{errors.email}</p>}
            </div>

            <div data-id="element-4883">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4884">
                Contraseña
              </label>
              <div className="relative" data-id="element-4885">
                <input type={showPassword ? 'text' : 'password'} placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} className={`w-full px-4 py-3 pr-11 border-2 rounded-xl transition-all outline-none focus:ring-4 focus:ring-teal-100 focus:border-teal-500 ${errors.password ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-4886" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1" data-id="element-4887">
                  {showPassword ? <EyeOff className="w-5 h-5" data-id="element-4888" /> : <Eye className="w-5 h-5" data-id="element-4889" />}
                </button>
              </div>
              {errors.password && <p className="text-xs text-red-600 mt-1.5" data-id="element-4890">{errors.password}</p>}
              {password && passwordStrength && <div className="mt-3" data-id="element-4891">
                  <div className="flex items-center gap-2 mb-1" data-id="element-4892">
                    <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-4893">
                      <div className={`h-full rounded-full transition-all duration-500 ${strengthColors[passwordStrength.level]}`} style={{
                    width: `${Math.min(100, passwordStrength.score / 7 * 100)}%`
                  }} data-id="element-4894" />
                    </div>
                    <span className={`text-xs font-semibold ${passwordStrength.level === 'weak' ? 'text-red-500' : passwordStrength.level === 'medium' ? 'text-amber-500' : 'text-emerald-500'}`} data-id="element-4895">
                      {passwordStrength.label}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-4896">
                    Mín. 8 caracteres, mayúscula, minúscula, número y símbolo
                  </p>
                </div>}
            </div>

            <div data-id="element-4897">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4898">
                Confirmar Contraseña
              </label>
              <div className="relative" data-id="element-4899">
                <input type={showConfirmPassword ? 'text' : 'password'} placeholder="••••••••" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className={`w-full px-4 py-3 pr-11 border-2 rounded-xl transition-all outline-none focus:ring-4 focus:ring-teal-100 focus:border-teal-500 ${errors.confirmPassword ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-4900" />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1" data-id="element-4901">
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" data-id="element-4902" /> : <Eye className="w-5 h-5" data-id="element-4903" />}
                </button>
              </div>
              {errors.confirmPassword && <p className="text-xs text-red-600 mt-1.5" data-id="element-4904">
                  {errors.confirmPassword}
                </p>}
            </div>

            <div className="flex items-start gap-3 p-4 bg-gradient-to-br from-teal-50/80 to-cyan-50/50 border border-teal-100 rounded-2xl" data-id="element-4905">
              <Shield className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" data-id="element-4906" />
              <div data-id="element-4907">
                <p className="text-xs text-teal-900 font-semibold mb-0.5" data-id="element-4908">
                  Información Segura y Confidencial
                </p>
                <p className="text-xs text-teal-700 leading-relaxed" data-id="element-4909">
                  Tu contraseña se almacena de forma segura con cifrado SHA-256.
                  Toda tu información médica está protegida.
                </p>
              </div>
            </div>

            <button type="submit" disabled={isSendingCode} className="w-full h-12 text-base font-semibold bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white shadow-lg shadow-teal-200/50 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4910">
              {isSendingCode ? 'Enviando código...' : 'Continuar'}
            </button>
          </form>

          <div className="mt-6 text-center" data-id="element-4911">
            <p className="text-sm text-gray-600" data-id="element-4912">
              ¿Ya tienes una cuenta?{' '}
              <button onClick={onBackToLogin} className="text-teal-600 font-semibold hover:text-teal-700 transition-colors" data-id="element-4913">
                Iniciar sesión
              </button>
            </p>
          </div>
        </div>

        <p className="text-center text-xs text-gray-500 mt-6 leading-relaxed" data-id="element-4914">
          Al crear una cuenta, recibirás un código de verificación en tu correo
          para confirmar tu identidad.
        </p>
      </div>
    </div>;
}